import {
  ApiBlueprint,
  AppRootElementBlueprint,
  createFrontendPlugin,
  discoveryApiRef,
  fetchApiRef,
  gitlabAuthApiRef,
  PageBlueprint,
} from '@backstage/frontend-plugin-api';
import { EntityContentBlueprint } from '@backstage/plugin-catalog-react/alpha';
import type { Entity } from '@backstage/catalog-model';
import { PipelineWatcher } from './PipelineWatcher';
import { GitlabSessionSignOut } from './mr/GitlabSessionSignOut';
import { createMergeRequestsApi, mergeRequestsApiRef } from './mr/api';
import { tabVisible } from '@platform/plugin-platform-react';

/**
 * L'onglet « Intégration (CI) » devient deux onglets simples, « Merge
 * Requests » et « Pipelines ». Ils ont d'abord formé un groupe d'onglets ;
 * les groupes ont été abandonnés le 2026-09-29 parce que Backstage les place
 * toujours en tête de la barre, avant « Vue d'ensemble ». Leur position est
 * fixée dans `app-config.yaml`.
 *
 * Il était rendu par `CodeAndCiTabs` (`entityShared.tsx`), une machine à
 * sous-onglets écrite à la main : une barre `Tabs` Material-UI, un index dans
 * un `useState`, une mémorisation dans `sessionStorage` et une
 * synchronisation `?tab=` ↔ URL. Tout ce code disparaît — c'est le gain le
 * plus net de la phase 4b.
 *
 * **Ce que ça change pour les liens existants :** une URL de la forme
 * `/code-ci?tab=pipelines` ne sélectionne plus rien. Elle ouvre « Merge
 * Requests », le paramètre étant désormais ignoré. Les pipelines ont leur
 * propre chemin, `/pipelines`, qui est partageable — ce que `?tab=` essayait
 * d'imiter. Le chemin `/code-ci` est conservé pour les Merge Requests, qui
 * étaient le sous-onglet par défaut : les liens les plus courants continuent
 * de fonctionner.
 */
const isComponent = (entity: Entity): boolean =>
  entity.kind?.toLowerCase() === 'component';

const mergeRequestsContent = EntityContentBlueprint.make({
  name: 'merge-requests',
  params: {
    path: '/code-ci',
    title: 'Merge Requests',
    filter: tabVisible('merge-requests', isComponent),
    loader: () =>
      import('./MergeRequestsContent').then(m => <m.MergeRequestsContent />),
  },
});

const pipelinesContent = EntityContentBlueprint.make({
  name: 'pipelines',
  params: {
    path: '/pipelines',
    title: 'Pipelines',
    filter: tabVisible('pipelines', isComponent),
    loader: () => import('./BuildContent').then(m => <m.BuildContent />),
  },
});

/** Client des routes `/mr/...` du backend (jeton GitLab de l'utilisateur transmis sans popup). */
const mergeRequestsApi = ApiBlueprint.make({
  name: 'merge-requests',
  params: defineParams =>
    defineParams({
      api: mergeRequestsApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        fetchApi: fetchApiRef,
        gitlabAuthApi: gitlabAuthApiRef,
      },
      factory: deps => createMergeRequestsApi(deps),
    }),
});

/**
 * Pages `/merge-requests/*` : le détail d'une MR (`/:project/:iid`), lien
 * partageable ouvert depuis la liste d'un composant ou depuis « Mes MR ».
 * Un sous-routeur plutôt qu'un chemin paramétré : « Mes MR »
 * occupe l'index `/merge-requests` de ce même routeur.
 * La page porte son propre en-tête : pas de barre de titre automatique.
 */
const mergeRequestsPage = PageBlueprint.make({
  name: 'merge-requests',
  params: {
    path: '/merge-requests',
    noHeader: true,
    loader: () =>
      import('./mr/MergeRequestsRouter').then(m => <m.MergeRequestsRouter />),
  },
});

/**
 * Surveille les pipelines des composants de l'utilisateur et publie des toasts
 * (API du socle). Vivait dans `PlatformNotifications`, dans `packages/app`.
 */
const pipelineWatcher = AppRootElementBlueprint.make({
  name: 'pipeline-watcher',
  params: { element: <PipelineWatcher /> },
});

/** Oublie le compte GitLab lié à la déconnexion (crochet du socle). */
const gitlabSessionSignOut = AppRootElementBlueprint.make({
  name: 'gitlab-session-sign-out',
  params: { element: <GitlabSessionSignOut /> },
});

export default createFrontendPlugin({
  pluginId: 'gitlab-ci',
  extensions: [
    mergeRequestsContent,
    pipelinesContent,
    mergeRequestsPage,
    mergeRequestsApi,
    pipelineWatcher,
    gitlabSessionSignOut,
  ],
});
